import { IApiClient, getApiClient } from "./ApiClient/ApiClient";
import { UserSecurityDetail } from "@/models/APIModels/UserSecurityDetail";
// import logger, { loggingSource } from "@/shared/asoftLogger";

import logger, { LoggingSource } from "@/shared/Logger";
import IResponseModel from "./ResponseModel";
import { User } from "@/models";
import UserPermissionItem from "@/models/APIModels/UserPermissionItem";

type LoginPayload = {
  emailAddress: string;
  password: string;
  device: string;
  ip: string;
  hostname: string;
  city: string;
  region: string;
  country: string;
  loc: string;
  org: string;
  postal: string;
  timezone: string;
};

export type SecurityResponse<T> = {
  accessToken: string;
  refreshToken: string;
  user: T;
};
export default class SecurityApi {
  private readonly _apiClient: IApiClient;
  private readonly URL = "/security";

  constructor(apiClient: IApiClient = getApiClient()) {
    this._apiClient = apiClient;
  }

  async login(payload: LoginPayload): Promise<IResponseModel<string>> {
    try {
      logger.info("Login...", LoggingSource.UISec);

      logger.debug(
        `Parameters => EmailAddress: ${payload.emailAddress}`,
        LoggingSource.UISec
      );
      logger.timeStart("Login call", LoggingSource.UISec);

      const result = await this._apiClient.post<LoginPayload, string>(
        this.URL + "/login",
        payload
      );

      logger.timeEnd("Login call", LoggingSource.UISec);
      logger.debug(
        `Login user: ${JSON.stringify(result)}`,
        LoggingSource.UISec
      );

      return result;
    } catch (error) {
      logger.timeEnd("Login call", LoggingSource.UISec);
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info("...Login", LoggingSource.UISec);
    }
  }

  async requestPasswordReset(
    emailAddress: string
  ): Promise<IResponseModel<void>> {
    try {
      logger.info("requestPasswordReset...", LoggingSource.UISec);

      logger.debug(
        `Parameter EmailAddress: ${emailAddress}`,
        LoggingSource.UISec
      );

      logger.timeStart("requestPasswordReset call", LoggingSource.UISec);
      const result = await this._apiClient.get<void>(
        `${this.URL}/requestpasswordreset?emailaddress=${emailAddress}`
      );
      logger.timeEnd("requestPasswordReset call", LoggingSource.UISec);

      logger.debug(
        `requestPasswordReset response: ${JSON.stringify(result)}`,
        LoggingSource.UISec
      );
      return result;
    } catch (error) {
      logger.timeEnd("requestPasswordReset call", LoggingSource.UISec);
      logger.error(JSON.stringify(error), LoggingSource.UISec);
      throw error;
    } finally {
      logger.info("...requestPasswordReset", LoggingSource.UISec);
    }
  }

  async resetPassword(
    token: string,
    emailAddress: string,
    password: string
  ): Promise<IResponseModel<void>> {
    try {
      logger.info("resetPassword...", LoggingSource.UISec);

      logger.debug(
        `Parameter Token: ${token}, EmailAddress: ${emailAddress}, Password: ${password}`,
        LoggingSource.UISec
      );

      logger.timeStart("resetPassword call", LoggingSource.UISec);
      const result = await this._apiClient.get<void>(
        `${this.URL}/resetPassword?token=${token}&emailaddress=${emailAddress}&password=${password}`
      );
      logger.timeEnd("resetPassword call", LoggingSource.UISec);

      logger.debug(
        `resetPassword response: ${JSON.stringify(result)}`,
        LoggingSource.UISec
      );
      return result;
    } catch (error) {
      logger.timeEnd("resetPassword call", LoggingSource.UISec);
      logger.error(JSON.stringify(error), LoggingSource.UISec);
      throw error;
    } finally {
      logger.info("...resetPassword", LoggingSource.UISec);
    }
  }

  async verifyMFAForLogin(
    verificationCode: string
  ): Promise<IResponseModel<SecurityResponse<UserSecurityDetail>>> {
    try {
      logger.info("VerifyMFAForLogin...", LoggingSource.UISec);

      logger.debug(
        `Parameters => VerificationCode: ${verificationCode}`,
        LoggingSource.UISec
      );
      logger.timeStart("VerifyMFAForLogin call", LoggingSource.UISec);

      const result = await this._apiClient.get<
        SecurityResponse<UserSecurityDetail>
      >(
        this.URL +
          `/verifysmschallengecodeforlogin?challengecode=${verificationCode}`
      );
      logger.timeEnd("VerifyMFAForLogin call", LoggingSource.UISec);
      logger.debug(`Response: ${JSON.stringify(result)}`, LoggingSource.UISec);
      return result;
    } catch (error) {
      logger.timeEnd("VerifyMFAForLogin call", LoggingSource.UISec);
      logger.error(JSON.stringify(error), LoggingSource.UISec);
      throw error;
    } finally {
      logger.info("...VerifyMFAForLogin", LoggingSource.UISec);
    }
  }

  async getSMSVerificationCode(
    token: string,
    userReference: string
  ): Promise<IResponseModel<{ mfaToken: string; mobilePhoneNumber: string }>> {
    try {
      logger.info("getSMSVerificationCode...", LoggingSource.UISec);

      logger.timeStart("getSMSVerificationCode call", LoggingSource.UISec);
      const result = await this._apiClient.get<{
        mfaToken: string;
        mobilePhoneNumber: string;
      }>(
        this.URL +
          `/newschoolofficerregistration/smschallengecode?userReference=${userReference}&token=${token}`
      );
      logger.timeEnd("getSMSVerificationCode call", LoggingSource.UISec);

      logger.debug(`response: ${JSON.stringify(result)}`, LoggingSource.UISec);
      return result;
    } catch (error) {
      logger.timeEnd("getSMSVerificationCode call", LoggingSource.UISec);
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info("...getSMSVerificationCode", LoggingSource.UISec);
    }
  }

  async updateToken(): Promise<
    IResponseModel<SecurityResponse<UserSecurityDetail>>
  > {
    try {
      logger.info("updateToken...", LoggingSource.UISec);

      logger.timeStart("updateToken call", LoggingSource.UISec);
      const result = await this._apiClient.get<
        SecurityResponse<UserSecurityDetail>
      >(this.URL + "/updatetoken");
      logger.timeEnd("updateToken call", LoggingSource.UISec);

      logger.debug(`response: ${JSON.stringify(result)}`, LoggingSource.UISec);
      return result;
    } catch (error) {
      logger.timeEnd("updateToken call", LoggingSource.UISec);
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info("...updateToken", LoggingSource.UISec);
    }
  }

  async logout(): Promise<IResponseModel<void>> {
    try {
      logger.info("Logout...", LoggingSource.UISec);

      logger.timeStart("Logout call", LoggingSource.UISec);
      const result = await this._apiClient.delete<void>(this.URL + "/logout");
      logger.timeEnd("Logout call", LoggingSource.UISec);
      return result;
    } catch (error) {
      logger.timeEnd("Logout call", LoggingSource.UISec);
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info("...Logout", LoggingSource.UISec);
    }
  }

  async sendEmailVerificationChallengeCode(
    mobilePhoneNumber: string,
    emailAddress: string,
    givenName: string,
    familyName: string
  ): Promise<IResponseModel<void>> {
    try {
      logger.info("sendEmailVerificationChallengeCode...", LoggingSource.UISec);

      logger.timeStart(
        "sendEmailVerificationChallengeCode call",
        LoggingSource.UISec
      );
      const result = await this._apiClient.get<void>(
        `${this.URL}/sendemailaddressverificationchallengecode?mobilePhoneNumber=${mobilePhoneNumber}&emailAddress=${emailAddress}&givenName=${givenName}&familyName=${familyName}`
      );
      logger.timeEnd(
        "sendEmailVerificationChallengeCode call",
        LoggingSource.UISec
      );
      return result;
    } catch (error) {
      logger.timeEnd(
        "sendEmailVerificationChallengeCode call",
        LoggingSource.UISec
      );
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info("...sendEmailVerificationChallengeCode", LoggingSource.UISec);
    }
  }

  async sendMobilehoneNumberVerificationChallengeCode(
    mobilePhoneNumber: string,
    emailAddress: string,
    givenName: string,
    familyName: string
  ): Promise<IResponseModel<void>> {
    try {
      logger.info(
        "sendMobilehoneNumberVerificationChallengeCode...",
        LoggingSource.UISec
      );

      logger.timeStart(
        "sendMobilehoneNumberVerificationChallengeCode call",
        LoggingSource.UISec
      );
      const result = await this._apiClient.get<void>(
        `${this.URL}/sendmobilephonenumberverificationchallengecode?mobilePhoneNumber=${mobilePhoneNumber}&emailAddress=${emailAddress}&givenName=${givenName}&familyName=${familyName}`
      );
      logger.timeEnd(
        "sendMobilehoneNumberVerificationChallengeCode call",
        LoggingSource.UISec
      );
      return result;
    } catch (error) {
      logger.timeEnd(
        "sendMobilehoneNumberVerificationChallengeCode call",
        LoggingSource.UISec
      );
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info(
        "...sendMobilehoneNumberVerificationChallengeCode",
        LoggingSource.UISec
      );
    }
  }

  async verifyEmailAddressChallengeCode(
    emailAddress: string,
    challengeCode: string
  ): Promise<IResponseModel<void>> {
    try {
      logger.info("verifyEmailAddressChallengeCode...", LoggingSource.UISec);

      logger.timeStart(
        "verifyEmailAddressChallengeCode call",
        LoggingSource.UISec
      );
      const result = await this._apiClient.get<void>(
        `${this.URL}/verifyemailaddresschallengecode?emailAddress=${emailAddress}&challengecode=${challengeCode}`
      );
      logger.timeEnd(
        "verifyEmailAddressChallengeCode call",
        LoggingSource.UISec
      );
      return result;
    } catch (error) {
      logger.timeEnd(
        "verifyEmailAddressChallengeCode call",
        LoggingSource.UISec
      );
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info("...verifyEmailAddressChallengeCode", LoggingSource.UISec);
    }
  }
  async verifyMobilePhoneNumberChallengeCode(
    mobilePhoneNumber: string,
    emailAddress: string,
    challengeCode: string
  ): Promise<IResponseModel<void>> {
    try {
      logger.info(
        "verifyMobilePhoneNumberChallengeCode...",
        LoggingSource.UISec
      );

      logger.timeStart(
        "verifyMobilePhoneNumberChallengeCode call",
        LoggingSource.UISec
      );
      const result = await this._apiClient.get<void>(
        `${this.URL}/verifymobilephonenumberchallengecode?mobilephonenumber=${mobilePhoneNumber}&emailAddress=${emailAddress}&challengecode=${challengeCode}`
      );
      logger.timeEnd(
        "verifyMobilePhoneNumberChallengeCode call",
        LoggingSource.UISec
      );
      return result;
    } catch (error) {
      logger.timeEnd(
        "verifyMobilePhoneNumberChallengeCode call",
        LoggingSource.UISec
      );
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info(
        "...verifyMobilePhoneNumberChallengeCode",
        LoggingSource.UISec
      );
    }
  }

  async isEasilyGuessedPassword(
    password: string
  ): Promise<IResponseModel<boolean>> {
    try {
      logger.info("isEasilyGuessedPassword...", LoggingSource.UISec);
      // Not Logging the password for security reasons
      logger.timeStart("isEasilyGuessedPassword call", LoggingSource.UISec);
      const result = await this._apiClient.get<boolean>(
        `${this.URL}/iseasilyguessedpassword?password=${password}`
      );
      logger.debug(
        `isEasilyGuessedPassword response: ${JSON.stringify(result)}`,
        LoggingSource.UISec
      );
      logger.timeEnd("isEasilyGuessedPassword call", LoggingSource.UISec);
      return result;
    } catch (error) {
      logger.timeEnd("isEasilyGuessedPassword call", LoggingSource.UISec);
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info("...isEasilyGuessedPassword", LoggingSource.UISec);
    }
  }

  async getUserPermissionsList(): Promise<
    IResponseModel<UserPermissionItem[]>
  > {
    try {
      logger.info("getUserPermissionsList...", LoggingSource.UISec);
      // Not Logging the password for security reasons
      logger.timeStart("getUserPermissionsList call", LoggingSource.UISec);
      const result = await this._apiClient.get<UserPermissionItem[]>(
        `${this.URL}/permissions`
      );
      logger.debug(
        `getUserPermissionsList response: ${JSON.stringify(result)}`,
        LoggingSource.UISec
      );
      logger.timeEnd("getUserPermissionsList call", LoggingSource.UISec);
      return result;
    } catch (error) {
      logger.timeEnd("getUserPermissionsList call", LoggingSource.UISec);
      logger.error(JSON.stringify(error), LoggingSource.UISec);

      throw error;
    } finally {
      logger.info("...getUserPermissionsList", LoggingSource.UISec);
    }
  }
}
